import { ConfigProvider, Flex, notification } from "antd";
import { AlertCard } from "@libertify/ui";
import { useDelete, useTranslate } from "@refinedev/core";
import { useCallback, useMemo, useState } from "react";
import { PanelSection } from "../PanelSection";
import { SettingsLabel } from "../../controls/SettingsLabel";
import { SegmentedButtons } from "../../controls/SegmentedButtons";
import { BrandkitAssetUploader } from "../../controls/BrandkitAssetUploader";
import { Volume } from "./features/Volume";
import { MusicAssetList } from "./features/MusicAssetList";
import { MusicAssetCard } from "./features/MusicAssetCard";
import { deriveMusicMode, MusicMode } from "./helpers";
import { useTemplateData } from "../../../hooks/useTemplateData";
import { useProjectInstance } from "pages/media/projects/hooks/useProjectInstance";
import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { useBrandKitEdit } from "pages/media/brandkit/hooks/useBrandkitEdit";
import { DEFAULT_MUSIC_VOLUME } from "../../../constants";
import { pollForBrandKitAsset } from "hooks/useBrandKit";
import { useAntTheme } from "hooks/useAntTheme";

export const AudioTab = () => {
  const t = useTranslate();
  const { theme } = useAntTheme();

  const selectedSegmentTheme = useMemo(
    () => ({
      components: {
        Segmented: {
          itemSelectedBg: theme.colorPrimary,
          itemSelectedColor: theme.colorTextLightSolid,
        },
      },
    }),
    [theme.colorPrimary, theme.colorTextLightSolid]
  );
  const {
    organizationId,
    projectId,
    instanceId,
    templatesData: { isFlashProject, projectMedia, brandKitId },
    mediaGenerationSettingsData: { mediaGenerationSettings, setMusicSettings },
    brandKitData: { brandKit, refetchBrandKit },
  } = useTemplateData();

  const music = useTemplateEditorStore((store) => store.templateState.music);

  const brandKitMusic = brandKit?.data.assets.find(
    (asset) => asset.purpose === "Music"
  );

  const { media: instanceMedia, updateSettings: updateInstanceSettings } =
    useProjectInstance({
      projectId: projectId ?? "",
      instanceId: instanceId ?? "",
      mediaId: projectMedia?.id ?? undefined,
    });

  const { mutateAsync: deleteAsset, isPending: isDeletingAsset } = useDelete();
  const brandKitIdOrUndefined = brandKitId ?? undefined;
  const {
    musicRefs,
    stagedAssets,
    setStagedAssets,
    handleEditSave,
    saving: isSavingBrandKitAsset,
  } = useBrandKitEdit(brandKitIdOrUndefined);

  const [showUploader, setShowUploader] = useState(false);
  const isEditingMusic = showUploader && !!brandKitMusic;
  // Covers the whole handleSaveMusicAsset chain (including the
  // refetchBrandKit() call after `saving` already flips false), so the
  // uploader keeps showing its uploading state instead of flashing the empty
  // dropzone before the real asset preview swaps in.
  const [isApplyingMusicAsset, setIsApplyingMusicAsset] = useState(false);

  const includeMusic = mediaGenerationSettings?.include_music;
  const musicAssetId = mediaGenerationSettings?.music_asset_id;
  const autoSelectMusic = instanceMedia?.auto_select_music;

  const derivedMode = useMemo(
    () =>
      deriveMusicMode({
        isFlashProject: !!isFlashProject,
        autoSelectMusic,
        includeMusic,
        musicAssetId,
        brandKitAssetId: brandKitMusic?.asset_id,
      }),
    [isFlashProject, autoSelectMusic, includeMusic, musicAssetId, brandKitMusic]
  );

  // The segment the user last clicked. Auto/Off/BrandKit(with an existing
  // asset) patch settings immediately on click. Upload and
  // BrandKit-without-an-asset have no settings to patch yet, so pendingMode
  // keeps the UI on the clicked segment until the user takes the follow-up
  // action (picking/uploading a track) that actually commits it.
  const [pendingMode, setPendingMode] = useState<MusicMode | null>(null);
  const mode = pendingMode ?? derivedMode;

  // Optimistic override for the Upload asset list: shows the clicked track as
  // selected immediately, before the settings write resolves. Cleared on
  // success (musicAssetId then reflects it anyway) and rolled back on
  // failure so the UI doesn't lie about what's actually saved.
  const [optimisticAssetId, setOptimisticAssetId] = useState<{
    assetId: string | null;
  } | null>(null);

  // Whether a mode/asset change triggered by this tab is still being applied.
  // Derived locally instead of from the underlying mutation hooks: those
  // toggle independently across the sequential auto_select_music + music
  // settings writes an apply can involve, which flickers the Segmented's
  // disabled state on and off between the two calls.
  const [isApplyingMusicChange, setIsApplyingMusicChange] = useState(false);

  const segmentOptions = useMemo(() => {
    const options = [
      {
        value: MusicMode.BrandKit,
        label: t(
          "components.templates.settings.musicTab.modes.brandKit"
        ).toUpperCase(),
      },
      ...(isFlashProject
        ? [
            {
              value: MusicMode.Auto,
              label: t(
                "components.templates.settings.musicTab.modes.auto"
              ).toUpperCase(),
            },
          ]
        : []),
      {
        value: MusicMode.Upload,
        label: t(
          "components.templates.settings.musicTab.modes.upload"
        ).toUpperCase(),
      },
      {
        value: MusicMode.Off,
        label: t(
          "components.templates.settings.musicTab.modes.off"
        ).toUpperCase(),
      },
    ];
    return options;
  }, [isFlashProject, t]);

  // Surfaces a failed settings write and, via the `finally` in each apply
  // function below, always releases pendingMode back to derivedMode — so a
  // failed click never leaves the UI stuck showing a segment that wasn't
  // actually saved.
  const showMusicSaveError = useCallback(() => {
    notification.error({
      message: t("components.templates.settings.musicTab.saveError"),
    });
  }, [t]);

  // Toggles auto_select_music to `nextAutoSelect` before writing music
  // settings, and reverts the toggle if the write fails — without this, a
  // successful instance-settings write followed by a failed music-settings
  // write leaves the two permanently out of sync on the BE.
  const applyMusicSettings = useCallback(
    async (nextAutoSelect: boolean, write: () => Promise<void>) => {
      const shouldToggleAutoSelect =
        isFlashProject && autoSelectMusic !== nextAutoSelect;
      let autoSelectMusicUpdated = false;
      try {
        if (shouldToggleAutoSelect) {
          await updateInstanceSettings({ auto_select_music: nextAutoSelect });
          autoSelectMusicUpdated = true;
        }
        await write();
      } catch (error) {
        if (autoSelectMusicUpdated) {
          await updateInstanceSettings({
            auto_select_music: !nextAutoSelect,
          }).catch((rollbackError) =>
            console.error(
              "Error rolling back auto_select_music after failed music settings save",
              rollbackError
            )
          );
        }
        throw error;
      }
    },
    [autoSelectMusic, isFlashProject, updateInstanceSettings]
  );

  const applyBrandKit = useCallback(async () => {
    if (!brandKitMusic) return;
    try {
      // asset id stays null so the brand kit's current music asset is always
      // resolved on the BE side, instead of pinning to the id known here.
      await applyMusicSettings(false, () => setMusicSettings(true, null));
    } catch (error) {
      showMusicSaveError();
      throw error;
    } finally {
      setPendingMode(null);
    }
  }, [applyMusicSettings, brandKitMusic, setMusicSettings, showMusicSaveError]);

  // Brand Kit clicked with no music asset available: there's nothing to
  // patch settings to except "no music", so we save that (clearing out
  // whatever include_music/music_asset_id was left from a previous mode)
  // but deliberately don't clear pendingMode — derivedMode has no way to
  // report BrandKit without a brand kit asset, so it would otherwise fall
  // back to Off and the uploader/warning would disappear right after save.
  const applyBrandKitNoAsset = useCallback(async () => {
    try {
      await applyMusicSettings(false, () => setMusicSettings(false, null));
    } catch (error) {
      showMusicSaveError();
      setPendingMode(null);
      throw error;
    }
  }, [applyMusicSettings, setMusicSettings, showMusicSaveError]);

  const applyAuto = useCallback(async () => {
    try {
      await applyMusicSettings(true, () => setMusicSettings(null, null));
    } catch (error) {
      showMusicSaveError();
      throw error;
    } finally {
      setPendingMode(null);
    }
  }, [applyMusicSettings, setMusicSettings, showMusicSaveError]);

  const applyOff = useCallback(async () => {
    try {
      await applyMusicSettings(false, () => setMusicSettings(false, null));
    } catch (error) {
      showMusicSaveError();
      throw error;
    } finally {
      setPendingMode(null);
    }
  }, [applyMusicSettings, setMusicSettings, showMusicSaveError]);

  // Called directly from the segment click. Upload doesn't patch here — it
  // waits for the user to pick a track. BrandKit always patches: with an
  // existing asset it selects it, without one it resets to "no music" so
  // stale settings from a previous mode can't linger.
  const handleModeChange = useCallback(
    (newMode: MusicMode) => {
      setPendingMode(newMode);
      // Upload has no immediate apply — it just waits for the user to pick a
      // track (handled separately by handleSelectAsset) — so it never marks
      // itself busy here, or nothing would ever clear the flag.
      if (newMode === MusicMode.Upload) return;

      setIsApplyingMusicChange(true);
      const stopApplying = () => setIsApplyingMusicChange(false);
      if (newMode === MusicMode.Auto) {
        applyAuto()
          .catch((error) => console.error("Error applying auto music", error))
          .finally(stopApplying);
      } else if (newMode === MusicMode.Off) {
        applyOff()
          .catch((error) =>
            console.error("Error applying no-music option", error)
          )
          .finally(stopApplying);
      } else if (newMode === MusicMode.BrandKit) {
        if (brandKitMusic) {
          applyBrandKit()
            .catch((error) =>
              console.error("Error applying brand kit music", error)
            )
            .finally(stopApplying);
        } else {
          applyBrandKitNoAsset()
            .catch((error) =>
              console.error("Error clearing music settings", error)
            )
            .finally(stopApplying);
        }
      }
    },
    [applyAuto, applyOff, applyBrandKit, applyBrandKitNoAsset, brandKitMusic]
  );

  const handleSelectAsset = useCallback(
    async (assetId: string | null) => {
      setOptimisticAssetId({ assetId });
      setIsApplyingMusicChange(true);
      try {
        await applyMusicSettings(false, () => setMusicSettings(true, assetId));
        setOptimisticAssetId(null);
        setPendingMode(null);
      } catch (error) {
        setOptimisticAssetId(null);
        showMusicSaveError();
        throw error;
      } finally {
        setIsApplyingMusicChange(false);
      }
    },
    [applyMusicSettings, setMusicSettings, showMusicSaveError]
  );

  const handleSaveMusicAsset = useCallback(async () => {
    const brandKitName = brandKit?.data.name;
    const palette = brandKit?.data.palette;
    if (!brandKitName || !palette) return;

    if (isEditingMusic && brandKitMusic.asset_id && organizationId) {
      await deleteAsset({
        resource: `media/${organizationId}/brand_kits/${brandKitId}/assets`,
        id: brandKitMusic.asset_id,
        successNotification: false,
      });
    }

    await handleEditSave(
      { name: brandKitName, colors: palette },
      () => undefined
    );

    const newMusicAsset = await pollForBrandKitAsset(refetchBrandKit, "Music");

    if (newMusicAsset) {
      // Same as applyBrandKit: leave asset id null, BE resolves the brand
      // kit's current music asset.
      await applyMusicSettings(false, () => setMusicSettings(true, null));
    }

    setShowUploader(false);
    setStagedAssets([]);
    // brandKitMusic (context, from an ancestor's query) won't reflect the
    // just-uploaded asset until that ancestor re-renders — same staleness
    // applyBrandKitNoAsset works around above. Pin to BrandKit instead of
    // clearing to derivedMode, which would still read brandKitAssetId as
    // empty and fall back to Off.
    setPendingMode(newMusicAsset ? MusicMode.BrandKit : null);
  }, [
    applyMusicSettings,
    brandKit,
    isEditingMusic,
    brandKitMusic,
    organizationId,
    brandKitId,
    deleteAsset,
    handleEditSave,
    refetchBrandKit,
    setMusicSettings,
    setStagedAssets,
  ]);

  const musicUploader = () => (
    <BrandkitAssetUploader
      mimeType="music"
      purpose="Music"
      brandKitAssets={brandKit?.data.assets}
      stagedAssets={stagedAssets}
      setStagedAssets={setStagedAssets}
      organizationId={organizationId}
      uploadRef={musicRefs.Music}
      brandKitId={brandKitIdOrUndefined}
      onSave={() => {
        setIsApplyingMusicAsset(true);
        handleSaveMusicAsset()
          .catch((error) => {
            console.error("Error saving brand kit music", error);
            setStagedAssets([]);
            musicRefs.Music.current?.reset();
          })
          .finally(() => setIsApplyingMusicAsset(false));
      }}
      isSaving={isSavingBrandKitAsset || isApplyingMusicAsset}
      isDeleting={isDeletingAsset}
      isEditing={isEditingMusic}
      onCancel={() => setShowUploader(false)}
    />
  );

  const brandkitMusicCard = () => {
    if (!brandKitMusic) return null;
    return showUploader ? (
      musicUploader()
    ) : (
      <MusicAssetCard
        asset={{
          id: brandKitMusic.asset_id,
          path: brandKitMusic.path,
          description: brandKitMusic.name,
        }}
        isBrandKit
        isSelected
        onSelect={() => undefined}
        volume={music?.volume ?? DEFAULT_MUSIC_VOLUME}
      />
    );
  };

  return (
    <Flex vertical style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <PanelSection
        title={t("components.templates.settings.musicTab.tabTitle")}
        subtitle={t("components.templates.settings.musicTab.description")}
        primary
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          justifyContent: "start",
        }}
      >
        {mode !== MusicMode.Off &&
          !(mode === MusicMode.BrandKit && !brandKitMusic) && <Volume />}
        <ConfigProvider theme={selectedSegmentTheme}>
          <SegmentedButtons<MusicMode>
            value={mode}
            options={segmentOptions}
            disabled={isApplyingMusicChange}
            onChange={handleModeChange}
          />
        </ConfigProvider>

        {mode === MusicMode.BrandKit &&
          (brandKitMusic ? (
            brandkitMusicCard()
          ) : (
            <>
              <AlertCard
                type="warning"
                title={t(
                  "components.templates.settings.musicTab.noBrandKitAsset.warning"
                )}
              />
              <SettingsLabel type="secondary">
                {t(
                  "components.templates.settings.musicTab.noBrandKitAsset.description"
                )}
              </SettingsLabel>
              {musicUploader()}
            </>
          ))}

        {mode === MusicMode.Auto && (
          <SettingsLabel type="secondary">
            {t("components.templates.settings.musicTab.autoDescription")}
          </SettingsLabel>
        )}
        {mode === MusicMode.Upload && (
          <MusicAssetList
            selectedAssetId={
              optimisticAssetId
                ? optimisticAssetId.assetId
                : (musicAssetId ?? null)
            }
            onSelectAsset={(assetId) => {
              handleSelectAsset(assetId).catch((error) =>
                console.error("Error selecting music asset", error)
              );
            }}
            volume={music?.volume ?? DEFAULT_MUSIC_VOLUME}
          />
        )}

        {mode === MusicMode.Off && (
          <SettingsLabel type="secondary">
            {t("components.templates.settings.musicTab.offDescription")}
          </SettingsLabel>
        )}
      </PanelSection>
    </Flex>
  );
};
